import { Autocomplete, Chip, Grid, TextField, Typography } from '@mui/material';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { ERROR_MSG_REQUIRED_FIELD, FORMAT_DATE } from 'common/Constant';
import { END_DATE_LATER_THAN_START_DATE } from 'common/error';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { validateDate } from 'utils/dateUtil';
import { fetchContactsByTenantCode } from '../action/action';

const MdRenewalCreationDetails = () => {
  const { control, getValues, setValue, formState } = useFormContext();
  const { tenantList } = useSelector((state) => state.mdRenewalManagement);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tenantInitialized, setTenantInitialized] = useState(false);
  const watchedTenant = useWatch({ control, name: 'tenant' });

  const hasUserInteractedRef = useRef(false);
  const lastFetchedTenantCodeRef = useRef(null);
  const lastChosenTenantRef = useRef(null);

  useEffect(() => {
    if (tenantList && Array.isArray(tenantList)) {
      const opts =
        tenantList
          .filter((tenant) => tenant.name)
          .map((tenant) => ({ id: tenant.id, name: tenant.name, code: tenant.code }))
          .sort((tenant1, tenant2) => (tenant1.name.toLowerCase() < tenant2.name.toLowerCase() ? -1 : 0));
      setTenantOptions(opts);
    } else {
      setTenantOptions([]);
    }
  }, [tenantList]);

  const fetchContactsForTenant = async (tenant) => {
    if (tenant && tenant.code) {
      try {
        if (lastFetchedTenantCodeRef.current === tenant.code) {
          return;
        }
        lastFetchedTenantCodeRef.current = tenant.code;
        const fetchContacts = fetchContactsByTenantCode(tenant.code);
        const contactsData = await fetchContacts();
        setContacts(Array.isArray(contactsData) ? contactsData : []);
      } catch (error) {
        setContacts([]);
      }
    } else {
      setContacts([]);
      lastFetchedTenantCodeRef.current = null;
    }
  };

  useEffect(() => {
    if (!tenantOptions?.length) return;
    setTenantInitialized(true);
  }, [tenantOptions]);

  useEffect(() => {
    if (!tenantOptions?.length || watchedTenant || !lastChosenTenantRef.current) return;
    setValue('tenant', lastChosenTenantRef.current, { shouldValidate: true, shouldDirty: false });
  }, [watchedTenant, tenantOptions]);


  const tenantFilterOptions = createFilterOptions({
    stringify: (option) => `${option.code} ${option.name}`
  });

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Controller
            name="tenant"
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                options={tenantOptions}
                filterOptions={tenantFilterOptions}
                getOptionLabel={(option) => (option && typeof option === 'object' ? (option.name || option.code || '') : '')}
                getOptionSelected={(option, value) => option?.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>{option.name}</li>
                )}
                onBlur={field.onBlur}
                    slotProps = {{
                          listbox: {
                              sx: {
                                '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ': {
                                  color: '#000000 !important'
                                },
                                '& .MuiAutocomplete-option[aria-selected="true"]': {
                                  backgroundColor: '#5470b8 !important',
                                },
                                '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                                  backgroundColor: '#f4f5f6 !important',
                                }
                              }
                          }
                      }}
                renderInput={(params) => {
                  params.inputProps = {
                    ...params.inputProps,
                    id: "input-tenant"
                  };
                  return (
                    <TextField
                      {...params}
                      label="Tenant"
                      error={tenantInitialized && !!fieldState?.error && (fieldState.isTouched || formState.isSubmitted)}
                      helperText={tenantInitialized && fieldState?.error && (fieldState.isTouched || formState.isSubmitted) ? fieldState.error.message : ''}
                    />
                  );
                }}
                onChange={(event, newValue) => {
                  hasUserInteractedRef.current = true;
                  lastChosenTenantRef.current = newValue || null;
                  field.onChange(newValue);
                  setValue('caseStartDate', '', { shouldValidate: true, shouldDirty: true });
                  setValue('caseEndDate', '', { shouldValidate: true, shouldDirty: true });
                  if (newValue && newValue.code) {
                    fetchContactsForTenant(newValue);
                  } else {
                    setContacts([]);
                  }
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={field.value || null}
              />
            )}
            rules={{
              validate: (value) => {
                if (!value) {
                  return ERROR_MSG_REQUIRED_FIELD;
                }
                if (typeof value === 'object' && !value.id) {
                  return ERROR_MSG_REQUIRED_FIELD;
                }
                return true;
              }
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="caseStartDate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField {...field} label="Plan Year Start" fullWidth placeholder={FORMAT_DATE} onBlur={field.onBlur} error={!!error} helperText={error?.message || ''} />
            )}
            rules={{
              required: {
                value: true,
                message: ERROR_MSG_REQUIRED_FIELD
              },
              validate: validateDate
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name="caseEndDate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField {...field} label="Plan Year End" fullWidth placeholder={FORMAT_DATE} onBlur={field.onBlur} error={!!error} helperText={error?.message || ''} />
            )}
            rules={{
              required: {
                value: true,
                message: ERROR_MSG_REQUIRED_FIELD
              },
              validate: (value) => {
                const errorMessage = validateDate(value);
                if (errorMessage !== true) return errorMessage;

                const startDate = new Date(getValues('caseStartDate'));
                const endDate = new Date(value);
                return endDate > startDate || END_DATE_LATER_THAN_START_DATE;
              }
            }}
          />
        </Grid>
      </Grid>

      {contacts.length > 0 && (
        <Grid item xs={12}>
          <div style={{ margin: '1rem 0' }}>
            <Typography variant="subtitle1" gutterBottom>
              Points of Contact:
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: '0.5rem' }}>
              {contacts.map((contact) => (
                <Chip
                  key={contact.username}
                  label={contact.fullName + ' <' + contact.email + '>'}
                />
              ))}
            </div>
          </div>
        </Grid>
      )}
    </>
  );
};

export default MdRenewalCreationDetails;